/**
 * Chord symbols: the harmony written over the staff.
 *
 * A chord symbol is not a chord. It names the harmony in force at a moment -- what a rhythm
 * section is to play and what a soloist is to play over -- and the notes under it may state
 * none of it. So a harmony is stored beside the music rather than inside it: a root, a kind
 * and an optional bass, standing at a moment of its bar.
 *
 * ## It is spelled, not counted
 *
 * The root is a step and an alter, as every pitch in this package is, because D flat seven
 * and C sharp seven are the same sound and different chords -- the first belongs in a flat
 * key and the second in a sharp one. Storing a pitch class and choosing a spelling on the
 * way out would pick the wrong one half the time, and it would make transposition ambiguous
 * exactly where a musician is most particular about it.
 *
 * ## The kind is named, not a set of intervals
 *
 * `major-seventh` is what MusicXML calls it and what a reader draws from, and `maj7` is what
 * a player types and reads. Both are here, in one table, so that neither is derived from the
 * other at a call site. What a kind is made of -- which degrees it adds or alters -- is not
 * modelled: no part of the editor plays a chord symbol or voices one, and a table of
 * intervals nobody reads is a table that drifts. The AI layer that will voice these chords
 * sits outside this package and brings its own theory.
 *
 * ## Where it stands
 *
 * `Harmony.onset` is ticks from the start of its own bar, not from the start of the piece,
 * so a bar that moves takes its chords with it. That is a position rather than an id, and it
 * is deliberate: a chord symbol stands at a beat whether or not a note begins there -- a bar
 * of rests under a chord chart is the ordinary case -- so there is nothing to anchor it to.
 *
 * A harmony is written where its part is written. On a transposing part that means the
 * player's key, which is what is printed over that player's staff.
 */

import { createId } from "./ids";
import type { HarmonyId } from "./ids";
import { STEPS, shiftPitch } from "./pitch";
import type { NotePitch, Step } from "./pitch";

/** A chord's root or bass: a letter and its accidental, with no octave to it. */
export interface ChordRoot {
  readonly step: Step;
  /** Semitones of accidental: a flat root is -1. */
  readonly alter: number;
}

/**
 * The chord kinds the editor writes, named as MusicXML names them.
 *
 * This is the working set of a lead sheet rather than every kind the format lists: the
 * alterations beyond it (`7♯11`, `13♭9`) are written in MusicXML as a kind plus a list of
 * altered degrees, which is a second model and a second parser for chords that can be typed
 * as a suffix once the need is real.
 */
export const HARMONY_KINDS = [
  "major",
  "minor",
  "augmented",
  "diminished",
  "power",
  "dominant",
  "major-seventh",
  "minor-seventh",
  "diminished-seventh",
  "augmented-seventh",
  "half-diminished",
  "major-minor",
  "major-sixth",
  "minor-sixth",
  "dominant-ninth",
  "major-ninth",
  "minor-ninth",
  "dominant-11th",
  "dominant-13th",
  "suspended-second",
  "suspended-fourth",
] as const;

export type HarmonyKind = (typeof HARMONY_KINDS)[number];

/** One chord symbol, standing at a moment of the bar that holds it. */
export interface Harmony {
  readonly id: HarmonyId;
  /** Ticks from the start of its own bar. */
  readonly onset: number;
  readonly root: ChordRoot;
  readonly kind: HarmonyKind;
  /** A bass other than the root: the `/A` of `F♯m7♭5/A`. */
  readonly bass: ChordRoot | null;
}

/**
 * How each kind is written after the root, and what else is accepted for it.
 *
 * The first spelling is the one printed; the rest are read. They are matched longest first,
 * so `m7b5` cannot be taken for an `m7` with rubbish after it, and `maj7` cannot be taken
 * for a bare `maj`.
 */
const SUFFIXES: Readonly<Record<HarmonyKind, readonly string[]>> = {
  major: ["", "maj", "M", "ma"],
  minor: ["m", "min", "-"],
  augmented: ["+", "aug"],
  diminished: ["dim", "o", "°"],
  power: ["5", "no3"],
  dominant: ["7", "dom7", "dom"],
  "major-seventh": ["maj7", "M7", "ma7", "Δ7", "Δ"],
  "minor-seventh": ["m7", "min7", "-7"],
  "diminished-seventh": ["dim7", "o7", "°7"],
  "augmented-seventh": ["+7", "aug7", "7#5"],
  "half-diminished": ["m7b5", "ø7", "ø", "min7b5", "-7b5"],
  "major-minor": ["mMaj7", "mM7", "minMaj7", "-maj7"],
  "major-sixth": ["6", "maj6", "M6"],
  "minor-sixth": ["m6", "min6", "-6"],
  "dominant-ninth": ["9", "dom9"],
  "major-ninth": ["maj9", "M9", "ma9", "Δ9"],
  "minor-ninth": ["m9", "min9", "-9"],
  "dominant-11th": ["11", "dom11"],
  "dominant-13th": ["13", "dom13"],
  "suspended-second": ["sus2"],
  "suspended-fourth": ["sus4", "sus"],
};

/** Every spelling, longest first, so that the longest match wins. */
const READINGS: readonly (readonly [string, HarmonyKind])[] = HARMONY_KINDS.flatMap((kind) =>
  SUFFIXES[kind].map((suffix) => [suffix, kind] as const),
).sort((left, right) => right[0].length - left[0].length);

/**
 * The kind a typed suffix names, or `undefined`.
 *
 * Case is tried first and only then ignored, because in chord symbols it carries meaning:
 * `Am` is A minor and `AM` is A major, and a reader that lower-cased both would turn every
 * minor chord typed with a capital into a major one. Ignoring it afterwards is what lets
 * `AMAJ7` and `CDIM` be read at all.
 */
function kindFor(suffix: string): HarmonyKind | undefined {
  const exact = READINGS.find(([spelling]) => spelling === suffix);
  if (exact !== undefined) return exact[1];
  const folded = suffix.toLowerCase();
  return READINGS.find(([spelling]) => spelling.toLowerCase() === folded)?.[1];
}

/** What is printed after the root for a kind: `maj7`, `m7b5`, or nothing at all. */
export function suffixOf(kind: HarmonyKind): string {
  return SUFFIXES[kind][0] ?? "";
}

/** A root as it is written: `F#`, `Bb`, `C`. */
export function rootText(root: ChordRoot): string {
  const alter = root.alter > 0 ? "#".repeat(root.alter) : "b".repeat(-root.alter);
  return `${root.step}${alter}`;
}

/** A whole symbol as it is written and as it may be typed back in. */
export function chordText(harmony: Harmony): string {
  const bass = harmony.bass === null ? "" : `/${rootText(harmony.bass)}`;
  return `${rootText(harmony.root)}${suffixOf(harmony.kind)}${bass}`;
}

/** The letter and accidental at the head of a symbol, and what is left of it. */
function readRoot(text: string): readonly [ChordRoot, string] | null {
  const step = text[0]?.toUpperCase();
  if (step === undefined || !STEPS.includes(step as Step)) return null;
  let alter = 0;
  let at = 1;
  for (; at < text.length; at += 1) {
    const mark = text[at];
    if (mark === "#" || mark === "♯") alter += 1;
    else if (mark === "b" || mark === "♭") alter -= 1;
    else break;
    // `b` is a letter as well as a flat: `Bb` is B flat, but the `b` of `Cbm` would be too,
    // and no chord spells a suffix with a leading `b`, so reading it as a flat is safe.
  }
  return [{ step: step as Step, alter }, text.slice(at)];
}

/**
 * Read a typed chord symbol, or say what is wrong with it.
 *
 * Typing is how a chord symbol is written in every notation editor -- there is no palette
 * of five hundred chords -- so this is the one door harmony comes in by, and its refusals
 * are shown to whoever typed them.
 */
export function parseChord(text: string): Harmony | string {
  const trimmed = text.trim();
  if (trimmed.length === 0) return "type a chord symbol, such as Cmaj7 or F#m7b5/A";

  const [before, over, ...rest] = trimmed.split("/");
  if (rest.length > 0 || before === undefined) return "a chord has one bass note at most";

  const head = readRoot(before);
  if (head === null) return `${trimmed} does not start with a note name`;
  const [root, suffix] = head;

  const kind = kindFor(suffix);
  if (kind === undefined) {
    return suffix.length === 0
      ? `${trimmed} is not a chord this editor knows`
      : `${suffix} is not a chord kind this editor knows`;
  }

  let bass: ChordRoot | null = null;
  if (over !== undefined) {
    const read = readRoot(over);
    if (read === null || read[1].length > 0) return `${over} is not a note name`;
    bass = read[0];
  }

  return { id: createId("harmony"), onset: 0, root, kind, bass };
}

/** The same chord a stated interval higher or lower, spelled as the interval says. */
export function shiftHarmony(harmony: Harmony, diatonic: number, chromatic: number): Harmony {
  return {
    ...harmony,
    root: shiftRoot(harmony.root, diatonic, chromatic),
    bass: harmony.bass === null ? null : shiftRoot(harmony.bass, diatonic, chromatic),
  };
}

/**
 * A root moved, borrowing the spelling rules a note is moved by.
 *
 * A root has no octave, so one is lent to it for the move and thrown away after: the
 * letter and the accidental are what a chord symbol is, and they are what come back. Doing
 * it this way rather than with a table of its own is what keeps a transposed chord spelled
 * the same as the notes under it.
 */
function shiftRoot(root: ChordRoot, diatonic: number, chromatic: number): ChordRoot {
  const lent: NotePitch = {
    ...root,
    octave: 4,
    accidental: "auto",
    tieStart: false,
    tieStop: false,
    drum: null,
    tab: null,
  };
  const moved = shiftPitch(lent, diatonic, chromatic);
  return { step: moved.step, alter: moved.alter };
}
